import { NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { TruncatePipe } from '../../../../../pipes/truncate.pipe';
import { ProjectUploadService } from '../../../../../services/project-upload.service';
import { ToastNotificationService } from '../../../../../services/toast-notification.service';
import { findFirstNonEmptyString } from '../../../../../utils';
import { MapCommunicatorService } from '../../../map-communicator.service';
import { DragDropDirective } from './directives/drag-drop.directive';

const MAX_FILE_SIZE_MB = 50; // Updated to match API limit
const MAX_TOTAL_FILES = 20;
const ALLOWED_EXTENSIONS = ['xlsx', 'csv', 'geojson', 'kml', 'gpkg', 'zip'];
const BYTES_IN_MB = 1024 * 1024;

interface UploadFile {
  file: File;
  progress: number;
  status?: string;
  uploaded?: boolean;
  error?: string;
}

@Component({
  selector: 'frmg-uploader',
  templateUrl: './uploader.component.html',
  styleUrls: ['./uploader.component.scss'],
  imports: [DragDropDirective, NgClass, MatButton, TruncatePipe]
})
export class UploaderComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input() projectId!: string;
  @Input() folderName!: string;
  @Input() tenantId?: string;
  @Input() teamId?: string;
  @Input() userId?: string;
  @Output() closeMenu = new EventEmitter<void>();

  files: UploadFile[] = [];

  constructor(
    private mapCommunicatorService: MapCommunicatorService,
    private projectUploadService: ProjectUploadService,
    private notificationService: ToastNotificationService,
    private cd: ChangeDetectorRef
  ) {}

  private extAllowed(name: string): boolean {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    return ALLOWED_EXTENSIONS.includes(ext);
  }

  private hasDuplicate(file: File): boolean {
    const key = `${file.name}__${file.size}__${file.lastModified}`;
    return this.files.some(
      (f) => `${f.file.name}__${f.file.size}__${f.file.lastModified}` === key
    );
  }

  private filterAllowed(fileList: FileList): {
    allowed: File[];
    rejected: string[];
  } {
    const allowed: File[] = [];
    const rejected: string[] = [];

    const remainingSlots = Math.max(0, MAX_TOTAL_FILES - this.files.length);

    for (const file of Array.from(fileList)) {
      if (allowed.length >= remainingSlots) {
        rejected.push(`${file.name} - Max total ${MAX_TOTAL_FILES} files`);
        continue;
      }
      if (!this.extAllowed(file.name)) {
        rejected.push(`${file.name} - Invalid extension`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_MB * BYTES_IN_MB) {
        rejected.push(`${file.name} - Too big file (> ${MAX_FILE_SIZE_MB}MB)`);
        continue;
      }
      if (this.hasDuplicate(file)) {
        rejected.push(`${file.name} - Already added`);
        continue;
      }
      allowed.push(file);
    }
    return { allowed, rejected };
  }

  fileBrowseHandler(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const { allowed, rejected } = this.filterAllowed(input.files);

    const dt = new DataTransfer();
    allowed.forEach((f) => dt.items.add(f));
    input.files = dt.files;

    if (allowed.length) {
      this.processFiles(input.files);
    }

    if (rejected.length) {
      this.notificationService.showNotification({
        type: 'error',
        message: `Some files are declined:\n• ${rejected.join('\n• ')}`,
        verticalPosition: 'bottom'
      });
    }

    input.value = '';
  }

  onFileDropped(fileList: FileList): void {
    const { allowed, rejected } = this.filterAllowed(fileList);

    if (allowed.length) {
      const dt = new DataTransfer();
      allowed.forEach((f) => dt.items.add(f));
      this.processFiles(dt.files);
    }

    if (rejected.length) {
      this.notificationService.showNotification({
        type: 'error',
        message: `Some files are declined:\n• ${rejected.join('\n• ')}`,
        verticalPosition: 'bottom'
      });
    }
  }

  private processFiles(fileList: FileList): void {
    const newFiles: UploadFile[] = Array.from(fileList).map((file) => ({
      file,
      progress: 0
    }));

    this.files.push(...newFiles);

    if (newFiles.length) {
      this.notificationService.showNotification({
        type: 'success',
        message: 'Files imported successfully.',
        verticalPosition: 'bottom'
      });
    }

    this.cd.markForCheck();
  }

  deleteFile(index: number): void {
    this.files.splice(index, 1);
  }

  upload(): void {
    for (const item of this.files.filter((f) => f.progress === 0 && !f.error)) {
      item.status = 'Sending';
      item.progress = 1;

      this.projectUploadService
        .uploadFile(
          this.projectId,
          item.file,
          false,
          this.tenantId,
          this.teamId,
          this.projectId, // folderId - use projectId as folderId
          this.userId
        )
        .subscribe({
          next: (response) => {
            item.status =
              response.status === 'processing' ? 'Processing' : 'Completed';
            item.uploaded = true;
            item.progress = 100;
            this.closeMenu.emit();

            // Show success message with upload details
            const message =
              response.status === 'processing'
                ? `File uploaded successfully. Processing ${response.total_records} records...`
                : `File processed successfully. ${response.valid_records} valid records, ${response.areas_created} areas created.`;

            this.notificationService.showNotification({
              type: 'success',
              message,
              verticalPosition: 'bottom'
            });

            setTimeout(() => {
              this.mapCommunicatorService.mapCommunicator.next('refresh_list');
            }, 3000);
            this.cd.detectChanges();
          },
          error: (error) => {
            item.status = 'Error';
            item.error = 'Upload failed';
            this.notificationService.showNotification({
              type: 'error',
              message: findFirstNonEmptyString(error.error) || item.error,
              verticalPosition: 'bottom'
            });
            this.cd.detectChanges();
          }
        });
    }
  }

  get filesToSendCount(): number {
    return this.files.filter((f) => f.progress === 0 && !f.error).length;
  }

  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }
}
